// src/components/KnowledgeGraphVisualization.js
import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3'; // Make sure d3 is installed (npm install d3)

const KnowledgeGraphVisualization = ({ graphData }) => {
    const svgRef = useRef();
    const containerRef = useRef(); // Ref for the container div

    // Memoize processed data to avoid recalculation on every render unless graphData changes
    const processedData = useMemo(() => {
        if (!graphData || !graphData.nodes || !graphData.links) {
            return { nodes: [], links: [] };
        }
        // Ensure links reference node objects, not just IDs (D3 simulation needs this)
        const nodeMap = new Map(graphData.nodes.map(node => [node.id, node]));
        const links = graphData.links
            .map(link => ({
                ...link,
                source: nodeMap.get(link.source), // Get source node object
                target: nodeMap.get(link.target)  // Get target node object
            }))
            .filter(link => link.source && link.target); // Filter out links with missing nodes

        // Assign initial positions if they don't exist (helps stabilize layout)
        graphData.nodes.forEach(node => {
            if (node.fx === undefined) node.fx = null; // Ensure fx/fy properties exist
            if (node.fy === undefined) node.fy = null;
        });


        return { nodes: graphData.nodes, links };
    }, [graphData]);


    useEffect(() => {
        if (!processedData.nodes.length || !svgRef.current || !containerRef.current) return;

        const container = d3.select(containerRef.current);
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove(); // Clear previous elements

        // Get container dimensions for centering
        const width = container.node().getBoundingClientRect().width;
        const height = container.node().getBoundingClientRect().height || 600; // Default height if needed

        svg.attr('width', width)
           .attr('height', height)
           .attr('viewBox', [-width / 2, -height / 2, width, height]); // Center the view

        // --- Setup Simulation ---
        const simulation = d3.forceSimulation(processedData.nodes)
            .force("link", d3.forceLink(processedData.links).id(d => d.id).distance(100)) // Adjust distance
            .force("charge", d3.forceManyBody().strength(-300)) // Adjust strength
            .force("center", d3.forceCenter(0, 0)) // Centered in the viewBox
            .force("collision", d3.forceCollide().radius(30)); // Prevent overlap

        // --- Draw Links ---
        const link = svg.append("g")
            .attr("stroke", "#999")
            .attr("stroke-opacity", 0.6)
            .selectAll("line")
            .data(processedData.links)
            .join("line")
            .attr("stroke-width", d => Math.sqrt(d.value || 1)); // Example: vary width by value

        // --- Draw Nodes ---
        const node = svg.append("g")
            .attr("stroke", "#fff")
            .attr("stroke-width", 1.5)
            .selectAll("circle")
            .data(processedData.nodes)
            .join("circle")
            .attr("r", 10) // Node radius
            .attr("fill", d => { // Color nodes by type (adjust colors/types)
                switch (d.type) {
                    case 'Goal': return '#1f77b4'; // Blue
                    case 'Initiative': return '#ff7f0e'; // Orange
                    case 'Issue': return '#d62728'; // Red
                    case 'ActionItem': return '#2ca02c'; // Green
                    default: return '#7f7f7f'; // Gray
                }
            })
            .call(drag(simulation)); // Enable dragging

        // --- Add Labels ---
        const labels = svg.append("g")
            .attr("class", "labels")
            .selectAll("text")
            .data(processedData.nodes)
            .join("text")
            .attr("text-anchor", "middle")
            .attr("dy", "-0.5em") // Position above the node
            .attr("font-size", "10px")
            .attr("fill", "#333")
            .text(d => d.label || d.name || d.id); // Display label/name/id

        // --- Add Tooltips (optional) ---
        node.append("title")
            .text(d => `${d.type}: ${d.label || d.name || d.id}\n${d.description || ''}`);

        // --- Simulation Ticker ---
        simulation.on("tick", () => {
            link
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => d.target.x)
                .attr("y2", d => d.target.y);

            node
                .attr("cx", d => d.x)
                .attr("cy", d => d.y);

            labels
                .attr("x", d => d.x)
                .attr("y", d => d.y);
        });


        // --- Drag Functionality ---
        function drag(simulation) {
            function dragstarted(event, d) {
                if (!event.active) simulation.alphaTarget(0.3).restart();
                d.fx = d.x;
                d.fy = d.y;
            }
            function dragged(event, d) {
                d.fx = event.x;
                d.fy = event.y;
            }
            function dragended(event, d) {
                if (!event.active) simulation.alphaTarget(0);
                // Keep position fixed after drag, or set fx/fy to null to let simulation continue
                // d.fx = null;
                // d.fy = null;
            }
            return d3.drag()
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended);
        }

        // --- Zoom Functionality (DISABLED) ---
        /* // Removed zoom definition
        const zoom = d3.zoom()
            .scaleExtent([0.1, 4]) // Zoom range
            .on("zoom", zoomed);
        */

        /* // Removed call to zoom
        svg.call(zoom);
        */

        /* // Removed zoom handler function
        function zoomed(event) {
            const { transform } = event;
            svg.selectAll('g').attr("transform", transform); // Apply transform to groups containing nodes, links, labels
        }
        */

        // --- Cleanup simulation on component unmount ---
        return () => {
            simulation.stop();
        };

    }, [processedData]); // Rerun effect if processedData changes


    return (
        // Container div to measure width/height
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <svg ref={svgRef}></svg>
        </div>
    );
};

export default KnowledgeGraphVisualization;
